/**
 * 批量事务编排器Hook - 管理复杂的数据获取流程
 *
 * 职责：
 * - 协调选择管理器和事务数据的交互
 * - 管理数据加载的时机和顺序
 * - 处理智能刷新逻辑
 * - 统一管理副作用的触发点
 */

import { useEffect, useCallback, useState, useRef } from "react";
import { DATA_LOADING_STATUS, CLIENT_STATUS, TRANSACTION_STATUS } from "../types";
import { PERMIT_TYPES } from "../../Monitor/constants/transactionConstants";
import { useBatchTransactionAPI } from "./useBatchTransactionAPI";

// 从合同项的tags确定许可类型
const determinePermitTypeFromTags = (tags) => {
    if (!tags || !Array.isArray(tags)) {
        return PERMIT_TYPES.OVERFLIGHT; // 默认为飞越许可
    }

    // 优先检查新的标签
    if (tags.includes("OVERFLIGHT")) {
        return PERMIT_TYPES.OVERFLIGHT;
    } else if (tags.includes("LANDING_TAKEOFF")) {
        return PERMIT_TYPES.LANDING_TAKEOFF;
    }
    // 兼容旧的标签 - 起飞和落地都归类为起降许可
    else if (tags.includes("DEPARTURE") || tags.includes("ARRIVAL")) {
        return PERMIT_TYPES.LANDING_TAKEOFF;
    } else {
        // 默认为飞越许可
        return PERMIT_TYPES.OVERFLIGHT;
    }
};

export const useBatchTransactionOrchestrator = (initialFlights, transactionType, selectionManager, transactionDataState, dispatch, actionCreators) => {
    const { initializeBatchTransactionData } = useBatchTransactionAPI();

    // 航班历史刷新状态
    const [historyRefreshTrigger, setHistoryRefreshTrigger] = useState(0);

    // 简化的加载状态管理 - 固定300ms显示
    const [transactionListLoading, setTransactionListLoading] = useState(false);

    // 触发航班历史刷新
    const triggerHistoryRefresh = useCallback(() => {
        console.log("🔄 触发航班历史刷新");
        setHistoryRefreshTrigger((prev) => prev + 1);
    }, []);

    // 显示事务列表加载状态（固定300ms）
    const showTransactionListLoading = useCallback(() => {
        setTransactionListLoading(true);
        setTimeout(() => {
            setTransactionListLoading(false);
        }, 300); // 固定300ms
    }, []);

    // 智能刷新函数：保持用户上下文的数据刷新
    const smartRefresh = useCallback(async () => {
        const { activeAirports, selectedSupplier } = selectionManager;
        const { selectedFlights } = transactionDataState;

        console.log("🔄🔄🔄 [SMART_REFRESH] 开始执行智能刷新 🔄🔄🔄");
        console.log("🔄 执行智能刷新，刷新事务数据和航班历史:", {
            activeAirports: activeAirports?.length,
            selectedSupplier: selectedSupplier?.name,
        });

        try {
            // 需要刷新事务数据
            if (activeAirports && activeAirports.length > 0 && selectedSupplier) {
                console.log("🔄 重新加载事务数据以同步最新状态");

                // 直接调用数据初始化，并拿到结果
                const result = await initializeBatchTransactionData(
                    initialFlights.map((f) => f.id),
                    transactionType,
                    selectedSupplier.id,
                    activeAirports,
                );

                if (result?.success) {
                    // 设置工作流模式
                    dispatch(actionCreators.setWorkflowMode(result.workflowMode));

                    // 基于后端过滤结果，构建对应航班集合
                    const filteredFlightIds = result.filteredFlightIds || result.flightIds || [];
                    const filteredFlights = selectedFlights.filter((flight) => filteredFlightIds.includes(flight.id));

                    // 映射为前端统一的事务结构
                    const allTransactions = filteredFlights.map((flight) => {
                        const existingTransaction = result.transactions?.find((t) => t.flightId === flight.id);

                        if (existingTransaction) {
                            return {
                                id: existingTransaction.id,
                                flightId: existingTransaction.flightId,
                                supplierId: existingTransaction.supplierId,
                                flightNumber: flight.flight_number || "",
                                flightDate: flight.flight_date || "",
                                route: `${flight.departure_airport || ""}-${flight.arrival_airport || ""}`,
                                departureAirport: flight.departure_airport || "",
                                arrivalAirport: flight.arrival_airport || "",
                                aircraftReg: flight.aircraft_reg || "",
                                status: existingTransaction.status,
                                remarks: existingTransaction.remarks,
                                batchId: existingTransaction.batchId,
                                additionalData: null,
                                createdAt: existingTransaction.createdAt,
                                updatedAt: existingTransaction.updatedAt,
                                originalData: {
                                    status: existingTransaction.status,
                                    remarks: existingTransaction.remarks,
                                    supplierId: existingTransaction.supplierId,
                                },
                                details: (existingTransaction.details || []).map((detail) => ({
                                    ...detail,
                                    currentStatus: detail.currentStatus || detail.status || TRANSACTION_STATUS.PENDING,
                                    clientStatus: CLIENT_STATUS.PRISTINE,
                                    originalData: JSON.parse(
                                        JSON.stringify({
                                            ...detail,
                                            currentStatus: detail.currentStatus || detail.status || TRANSACTION_STATUS.PENDING,
                                        }),
                                    ),
                                })),
                            };
                        } else {
                            // 没有事务则创建空事务骨架
                            return {
                                id: `new_${flight.id}`,
                                flightId: flight.id,
                                supplierId: selectedSupplier?.id || null,
                                flightNumber: flight.flight_number,
                                flightDate: flight.flight_date,
                                route: `${flight.departure_airport}-${flight.arrival_airport}`,
                                departureAirport: flight.departure_airport,
                                arrivalAirport: flight.arrival_airport,
                                aircraftReg: flight.aircraft_reg,
                                status: null,
                                remarks: null,
                                batchId: null,
                                additionalData: null,
                                createdAt: new Date().toISOString(),
                                updatedAt: new Date().toISOString(),
                                details: [],
                                isNewTransaction: true,
                            };
                        }
                    });

                    // 应用到全局状态
                    dispatch(actionCreators.setTransactions(allTransactions));

                    // 提取已存在明细的产品，更新选中产品以保持UI一致
                    if (result.workflowMode === "EDIT" && allTransactions.length > 0) {
                        const contractItems = allTransactions.flatMap((t) =>
                            t.details.map((d) => ({
                                id: d.contractItemId,
                                product_name: d.productName,
                                partner_name: d.supplierName,
                                price: d.unitPrice,
                                currency: d.currency,
                                product_unit: d.unit,
                            })),
                        );
                        const uniqueContractItems = contractItems.filter((item, index, self) => index === self.findIndex((i) => i.id === item.id));
                        selectionManager.setSelectedContractItems(uniqueContractItems);
                    }

                    console.log("✅ 事务数据刷新完成 (smartRefresh)");
                } else {
                    console.warn("⚠️ 初始化结果失败或为空，跳过事务刷新");
                }
            }

            // 触发航班历史刷新
            triggerHistoryRefresh();

            console.log("✅ 智能刷新完成");
        } catch (error) {
            console.error("❌ 智能刷新失败:", error);
            // 即使事务数据刷新失败，也要尝试刷新航班历史
            triggerHistoryRefresh();
        }
    }, [initialFlights, transactionType, selectionManager, transactionDataState, dispatch, actionCreators, initializeBatchTransactionData, triggerHistoryRefresh]);

    return {
        // 状态
        historyRefreshTrigger,
        transactionListLoading,

        // 操作方法
        triggerHistoryRefresh,
        showTransactionListLoading,
        smartRefresh,
    };
};
